import { KomojuPaymentStatus, XsollaPaymentStatus } from './api/PaymentsAPI';
import { PurchaseInfo, ReplicantAsyncActionAPI } from './api/ReplicantAPI';
import { Entry } from './db/DB';
import { ComputedProperties } from './ReplicantComputedProperties';
import { PartialReplicant, Replicant } from './ReplicantConfig';
import type { ReplicantModule, ReplicantWithModules } from './ReplicantModules';
import { ABTestBucket, EmptyRuleset, Ruleset } from './ReplicantRuleset';
import type { SharedStates, SharedStatesAsyncGetterAPI } from './ReplicantSharedStates';
import { SocialGraphNonPlayer } from './ReplicantSocialGraph';
import SB from './SchemaBuilder';
import type { AtoneServiceType, AtoneTransaction } from './server/Atone';
import { WithMeta } from './systemStateFields';
import type { Immutable, Namespaced } from './utils/TypeUtils';

export type AsyncGetters<
    TState,
    TRuleset extends Ruleset = EmptyRuleset,
    TComputedProperties extends ComputedProperties = {},
    TSharedStates extends SharedStates = {},
    TModules extends ReplicantModule[] = [],
> = Namespaced<AsyncGetter<TState, TRuleset, TComputedProperties, TSharedStates, TModules>>;

export type AsyncGetter<
    TState,
    TRuleset extends Ruleset = EmptyRuleset,
    TComputedProperties extends ComputedProperties = {},
    TSharedStates extends SharedStates = {},
    TModules extends ReplicantModule[] = [],
> = (
    args: any,
    api: AsyncGettersAPI<
        ReplicantWithModules<
            PartialReplicant<{
                state: TState;
                ruleset: TRuleset;
                computedProperties: TComputedProperties;
                sharedStates: TSharedStates;
            }>,
            TModules
        >
    >,
) => Promise<any>;

/** @see https://stripe.com/docs/payments/intents#intent-statuses */
export type PaymentIntentStatus =
    | 'canceled'
    | 'processing'
    | 'requires_action'
    | 'requires_capture'
    | 'requires_confirmation'
    | 'requires_payment_method'
    | 'succeeded';

export type AsyncGettersAPI<T extends Replicant> = Pick<
    ReplicantAsyncActionAPI<T>,
    | 'math'
    | 'date'
    | 'fetch'
    | 'fetchStates'
    | 'getSessionID'
    | 'getUserAssetUrl'
    | 'getUserID'
    | 'internalKvStore'
    | 'kvStore'
    | 'searchPlayers'
    | 'telegram'
    | 'countPlayers'
    | 'reportError'
    | 'sendAnalyticsEvents'
> & {
    /**
     * Methods for interacting with the Airtable configuration service.
     *
     * Airtable environment variables must be configured before usage: see docs in https://docs.dev.gc-internal.net/replicant/Environment/.
     *
     * Disabled in offline mode.
     */
    airtable: {
        /**
         * Fetch table items from Airtable.
         *
         * Changes made in Airtable can take up to 15 minutes to propagate here due to caching.
         *
         * @returns List of table items or `null` if table is not found.
         */
        getTable(tableName: string): Promise<{ [key: string]: unknown }[] | null>;
    };

    chatbot: {
        /**
         * @returns A promise that resolves to `true` if user is subscribed to the platform chatbot.
         *
         * Only enabled on the LINE platform.
         */
        isSubscribed: () => Promise<boolean>;
    };

    /** Return a list of past IAP transactions. */
    getOwnPurchaseHistory: () => Promise<Immutable<PurchaseInfo[]>>;

    /**
     * The return value is cached so the method can be safely called multiple times in a single async getter.
     *
     * Note that any messages sent and flushed with `api.flushMessages` in the same action are not reflected in the return value.
     *
     * @returns Player state.
     */
    getOwnState: () => Promise<WithMeta<Immutable<T['state']>, T['ruleset'], T['sharedStates']>>;

    /**
     * @returns A promise that resolves to `true` if the current user is a web-playable user that has been converted to a platform user.
     *
     * @see https://docs.dev.gc-internal.net/replicant/basic-features/Modes#convert-web-playable-user-to-platform-user
     */
    hasPlatformUser: () => Promise<boolean>;

    /** Methods for interacting with payments. */
    payments: {
        /**
         * Generate an Atone (deferred payment service) [payment checksum](https://developers.aftee.tw/v1/global/reference-en/boku-index.html#1-aftee-system-kit-2).
         *
         * The [`ATONE_SECRET_KEY` environment variable](https://docs.dev.gc-internal.net/replicant/Environment/) must be configured when using Atone.
         */
        generateAtonePaymentChecksum: (paymentData: { [key: string]: unknown }) => Promise<string>;

        /**
         * Look up an Atone (deferred payment service) transaction.
         *
         * The [`ATONE_SECRET_KEY` environment variable](https://docs.dev.gc-internal.net/replicant/Environment/) must be configured when using Atone.
         *
         * @param transactionId Transaction ID returned by the Atone client SDK on payment creation.
         * @param serviceType Atone service type used on payment creation.
         * @returns Transaction information or `null` if transaction is not found.
         */
        getAtoneTransaction: (transactionId: string, serviceType: AtoneServiceType) => Promise<AtoneTransaction | null>;

        /**
         * Check the status of a Stripe Checkout Session created with the `api.payments.createCheckoutSession` async action API.
         *
         * The [`STRIPE_API_SECRET` environment variable](https://docs.dev.gc-internal.net/replicant/Environment/) must be configured when using Stripe Checkout.
         *
         * @returns Checkout Session status or `null` if Checkout Session is not found.
         */
        getCheckoutSessionStatus: (opts: { checkoutSessionId: string }) => Promise<{
            /** @see https://stripe.com/docs/api/checkout/sessions/object#checkout_session_object-payment_status */
            paymentStatus: 'paid' | 'unpaid';

            /** @see https://stripe.com/docs/api/checkout/sessions/object#checkout_session_object-payment_status */
            status: 'open' | 'complete' | 'expired';
        } | null>;

        /**
         * Check the status of a Komoju payment created with the `api.payments.createKomojuPayment` async action API.
         *
         * The [`KOMOJU_SECRET_KEY` environment variable](https://docs.dev.gc-internal.net/replicant/Environment/) must be configured when using Komoju.
         *
         * @param komojuPaymentId Komoju payment ID returned by the `api.payments.createKomojuPayment` async action API.
         * @returns Komoju payment or `null` if payment is not found.
         */
        getKomojuPayment: (komojuPaymentId: string) => Promise<{ status: KomojuPaymentStatus } | null>;

        /**
         * Delete a Komoju payment created with the `api.payments.createKomojuPayment` async action API.
         *
         * The [`KOMOJU_SECRET_KEY` environment variable](https://docs.dev.gc-internal.net/replicant/Environment/) must be configured when using Komoju.
         *
         * @param komojuPaymentId Komoju payment ID returned by the `api.payments.createKomojuPayment` async action API.
         * @returns Komoju payment or `null` if payment is not found.
         */
        cancelKomojuPayment: (komojuPaymentId: string) => Promise<{ status: KomojuPaymentStatus } | null>;

        /**
         * Check the status of a Xsolla order created with the `api.payments.createXsollaPaymentToken` async action API.
         *
         * `undefined` is returned if the order is not found, or if there was an error fetching the order.
         */
        getXsollaOrder: (opts: {
            token: string;
            orderId: number;
        }) => Promise<{ status: XsollaPaymentStatus } | undefined>;

        /**
         * Check the status of a Stripe payment created with the `api.payments.createInvoice` async action API.
         *
         * @returns Payment Intent status or `null` if Payment Intent is not found.
         */
        getPaymentStatus: (opts: {
            paymentIntentId: string;

            /**
             * Stripe account ID. Define only when making API calls on behalf of a 3rd party game's [connected Stripe account](https://stripe.com/docs/billing/subscriptions/connect).
             *
             * @default `undefined`, uses Playco's Stripe account.
             */
            stripeAccountId?: string;
        }) => Promise<{
            /**
             * Credit card brand, for example `amex`, `mastercard`, `visa`, ...
             *
             * @see https://stripe.com/docs/api/payment_methods/object#payment_method_object-card-brand
             */
            creditCardBrand?: string;
            /**
             * Payment method, for example `card` or `paypal`.
             *
             * @see https://stripe.com/docs/api/payment_methods/object#payment_method_object-type
             */
            paymentMethod?: string;

            paymentStatus: PaymentIntentStatus;

            /**
             * Card wallet type, for example `apple_pay` or `google_pay`.
             *
             * @see https://stripe.com/docs/api/payment_methods/object#payment_method_object-card-wallet-type
             */
            wallet?: string;
        } | null>;
    };

    purchases: {
        /**
         * Get all the Telegram purchases ready for consumption.
         *
         * @see https://docs.dev.gc-internal.net/replicant/platform-guides/telegram-integration#telegram-stars-payments
         *
         * @returns A Promise that resolves with a map of product IDs to the number of unconsumed purchases.
         */
        getUnconsumedPurchases: () => Promise<{ [productId: string]: number }>;
    };

    socialGraph: {
        /** @returns Non-playing users along with their playing friends and received social interactions. */
        getNonPlayers: (nonPlayerIds: string[]) => Promise<SocialGraphNonPlayer[]>;
    };

    /**
     * @returns All of the current player's state recovery snapshots.
     * @internal
     */
    getSnapshots: () => Promise<Entry<{ [key: string]: unknown }>[]>;

    abTests: {
        getBucketID<TABTestID extends keyof T['ruleset']['abTests'] & string>(
            testId: TABTestID,
        ): Promise<ABTestBucket<NonNullable<T['ruleset']['abTests']>, TABTestID> | undefined>;
    };

    sharedStates: SharedStatesAsyncGetterAPI<T['sharedStates']>;
};

export type AsyncGetterInvokeFn<T extends (...args: any[]) => any> = (args: Parameters<T>[0]) => ReturnType<T>;

export type AsyncGettersCallables<T extends AsyncGetters<any>> = {
    [K in keyof T]: T[K] extends (...args: any[]) => any
        ? AsyncGetterInvokeFn<T[K]>
        : T[K] extends infer NestedT extends Record<string, any>
        ? { [NestedK in keyof NestedT]: AsyncGetterInvokeFn<NestedT[NestedK]> }
        : never;
};

export function createAsyncGetters<
    TState,
    TRuleset extends Ruleset = EmptyRuleset,
    TComputedProperties extends ComputedProperties = {},
    TSharedStates extends SharedStates = {},
    TModules extends ReplicantModule[] = [],
>(
    state: SB.Schema<TState>,
    config?: {
        ruleset?: TRuleset;
        computedProperties?: TComputedProperties;
        sharedStates?: TSharedStates;
        modules?: TModules;
    },
) {
    return <TAsyncGetters extends AsyncGetters<TState, TRuleset, TComputedProperties, TSharedStates, TModules>>(
        asyncGetters: TAsyncGetters,
    ) => asyncGetters;
}
